import React, { useState, useEffect, useRef } from "react";
import { AddPropertyForm } from "./AddPropertyPage";
import PropertyTypeModal from "../../../components/properties/PropertyTypeModal";
import { importCollingsProperty, getCollingsProperty, updateCollingsProperty, getCollingsProperties, uploadCollingsPropertyFloorplan } from "../../../api/collingsProperties";
import { checkUploadStatus } from "../../../api/rea";
import { registerProperty, updateProperty, getProperties } from "../../../api/agent";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { formatREAError, extractErrorsFromResponse, formatErrorForToast } from "../../../utils/reaErrorFormatter";
import { localRecordFailureMessage } from "../utils/localRecordFailure";
import { sanitizePropertyData } from "../../../utils/textSanitizer";
import { validateCollingsProperty } from "../../../utils/collingsPropertyValidation";
import ConfirmModal from "../../../components/common/ConfirmModal";
import {
  formatMelbourneDatePart,
  formatMelbourneTimePart,
  datetimeLocalToUtcISOString,
} from "../../../utils/melbourneTime";

// The Collings Properties upload (property-crawler) needs street_number and
// street as separate fields - unlike property_management, which just stores
// one free-text "address". The shared AddPropertyForm's own "Street" field is
// optional and rarely filled in (it's for a specific internal-marketing
// display mode, not required here), so it can't be relied on. Split the
// street line (the part of the full address before the suburb) into number +
// name instead. Sending the whole address as "street" left street_number
// holding the entire raw string and street holding just the suburb (REA then
// rejected the listing outright, and the mangled address showed on the live
// site) - live report, 2026-08-14: 8/118 Miller Street, Fitzroy North.
const splitStreetLine = (address) => {
    const line = (address || "").split(",")[0].trim();
    const match = line.match(/^([0-9A-Za-z]+(?:[/-][0-9A-Za-z]+)*)\s+(.*)$/);
    if (!match) return { streetNumber: "", street: line };
    return { streetNumber: match[1], street: match[2].trim() };
};

const getInitialStatusForListingType = (type) => {
    if (type === "appraisal") return "Appraisal";
    if (type === "offmarket") return "Off Market Listing";
    if (type === "onmarket") return "On Market Listing";
    return "On Market Listing";
};

// Sum the typed parking entries (the form's `parking_list`) into a single total.
// The Collings/REA save path historically read the legacy `parking` field, which
// AddPropertyForm no longer populates — so the car count was always sent as 0 and
// the "car number" silently disappeared on save. Falls back to the legacy
// single-value fields when no typed list is present (older data / other callers).
const deriveParkingTotal = (data) => {
    const list = Array.isArray(data?.parking_list) ? data.parking_list : [];
    const totalFromList = list.reduce((sum, item) => sum + (parseInt(item?.spaces, 10) || 0), 0);
    if (totalFromList > 0) return totalFromList;
    const legacy = parseInt(data?.parking_spaces ?? data?.parking, 10);
    return Number.isFinite(legacy) ? legacy : 0;
};

// Split the same typed parking_list by type (garage/carport/open) instead of
// collapsing it into one combined number - mirrors AddPropertyPage's own
// split so a Collings property's garage vs carport count survives the save
// instead of every carport silently being counted as (or replacing) a garage
// (CG report, 2026-08-06: REA showed 1 car spot for a CRM entry of 2).
const deriveParkingTotals = (data) => {
    const list = Array.isArray(data?.parking_list) ? data.parking_list : [];
    return list.reduce(
        (acc, item) => {
            const count = parseInt(item?.spaces, 10) || 0;
            if (item?.type === "garage") acc.garage += count;
            else if (item?.type === "carport") acc.carport += count;
            else if (item?.type === "open") acc.open += count;
            return acc;
        },
        { garage: 0, carport: 0, open: 0 }
    );
};

export default function AddCollingsPropertyPage() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [isUpdating, setIsUpdating] = useState(false);
    const [initialValues, setInitialValues] = useState(null);
    const [isLoading, setIsLoading] = useState(!!id);
    const [showTypeModal, setShowTypeModal] = useState(!id);
    const [selectedListingType, setSelectedListingType] = useState(null);
    const [createdPropertyId, setCreatedPropertyId] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    // Store REA identifiers when editing - CRITICAL for syncing updates to third-party (REA)
    const [existingReaData, setExistingReaData] = useState(null);
    const [showReaConfirm, setShowReaConfirm] = useState(false);
    const [pendingCollingsData, setPendingCollingsData] = useState(null);
    const reaConfirmedRef = useRef(false);

    useEffect(() => {
        if (id) {
            const fetchProperty = async () => {
                try {
                    setIsLoading(true);
                    const res = await getCollingsProperty(id);
                    const data = res.data;

                    // Transform for form
                    const formValues = {
                        title: data.headline || "",
                        address: data.street || "",
                        suburb: data.suburb || "",
                        city: data.suburb || "",
                        state: data.state || "",
                        postal_code: data.postcode || "",
                        price: data.price || "",
                        beds: data.features?.bedrooms || "",
                        baths: data.features?.bathrooms || "",
                        parking: data.features?.garages || "",
                        // `parkings` seeds AddPropertyForm's typed parking_list (garage row) so the
                        // car count reloads into the editor instead of resetting to empty.
                        parkings: data.features?.garages || data.extra_info?.parking_spaces || "",
                        description: data.description || "",
                        status: (data.extra_info?.status || "").toLowerCase().includes("offmarket") ? "Off Market Listing" :
                            (data.extra_info?.status || "").toLowerCase().includes("prospect") ? "Prospect / Not Currently Listed" :
                                (data.extra_info?.status || "").toLowerCase().includes("appraisal") ? "Appraisal" : "On Market Listing",
                        propertyTypes: data.property_type || data.category || "House",
                        images: (data.images || data.extra_info?.image_urls || data.extra_info?.images || []).map(url => (typeof url === 'string' ? { file: url } : { file: url?.url || url?.file || url })),
                        // Map agents: use full agent objects from extra_info (like public properties)
                        // Fallback to agents array or agent_id for backward compatibility
                        agentId: data.agent?.id ? String(data.agent.id) :
                            (data.extra_info?.agent?.id ? String(data.extra_info.agent.id) :
                                (data.agents?.[0] ? String(typeof data.agents[0] === 'object' ? data.agents[0].id : data.agents[0]) :
                                    (data.extra_info?.agent_id ? String(data.extra_info.agent_id) : ""))),
                        secondaryAgent: data.co_agent?.id ? String(data.co_agent.id) :
                            (data.extra_info?.co_agent?.id ? String(data.extra_info.co_agent.id) :
                                (data.agents?.[1] ? String(typeof data.agents[1] === 'object' ? data.agents[1].id : data.agents[1]) : "")),
                        tertiaryAgent: data.tertiary_agent?.id ? String(data.tertiary_agent.id) :
                            (data.extra_info?.tertiary_agent?.id ? String(data.extra_info.tertiary_agent.id) :
                                (data.agents?.[2] ? String(typeof data.agents[2] === 'object' ? data.agents[2].id : data.agents[2]) : "")),
                        // Map fields from extra_info
                        method_of_sale: data.extra_info?.method || data.method_of_sale || "Private Sale",
                        parking_type: data.extra_info?.parking_type || data.parking_type || "",
                        parking_spaces: data.extra_info?.parking_spaces || data.parking_spaces || data.features?.garages || "",
                        floor_area: data.extra_info?.floor_area || data.extra_info?.area || data.floor_area || "",
                        land_area: data.extra_info?.land_area || data.land_area || "",
                        // Stored value is a UTC ISO string; the shared form's
                        // datetime-local input needs Melbourne wall-clock time.
                        auction_date: (() => {
                            const raw = data.extra_info?.auction_date || data.auction_date || "";
                            if (!raw) return "";
                            return `${formatMelbourneDatePart(raw)}T${formatMelbourneTimePart(raw)}`;
                        })(),
                        auction_venue: data.extra_info?.auction_venue || "",
                        year_built: data.extra_info?.year_built || "",
                        property_class: data.extra_info?.property_class || "",
                        commercial_category: data.commercial_category || data.extra_info?.commercial_category || "",
                        commercial_category_2: data.commercial_category_2 || data.extra_info?.commercial_category_2 || "",
                        commercial_category_3: data.commercial_category_3 || data.extra_info?.commercial_category_3 || "",
                        video_url: data.extra_info?.video_url || "",
                        virtual_tour_link: data.extra_info?.virtual_tour_link || "",
                        transaction_type: data.extra_info?.transaction_type || "sale",
                        price_display: data.extra_info?.price_display || "exact",
                        commission_type: data.extra_info?.commission_type || (data.extra_info?.commission_amount != null && data.extra_info?.commission_amount !== "" ? "fixed" : "percentage"),
                        commission_rate: data.extra_info?.commission_rate || "",
                        commission_amount: (data.extra_info?.commission_amount != null && data.extra_info?.commission_amount !== "") ? (typeof data.extra_info.commission_amount === "string" && data.extra_info.commission_amount.startsWith("$") ? data.extra_info.commission_amount : `$${Number(data.extra_info.commission_amount).toLocaleString()}`) : "",
                        est_gross_yield: data.extra_info?.est_gross_yield || "",
                        est_outgoings: data.extra_info?.est_outgoings || "",
                        est_net_yield: data.extra_info?.est_net_yield || "",
                        body_corporate: data.extra_info?.body_corporate ? String(data.extra_info.body_corporate) : "",
                        council_rates: data.extra_info?.council_rates ? String(data.extra_info.council_rates) : "",
                        water_rates: data.extra_info?.water_rates ? String(data.extra_info.water_rates) : "",
                        current_annual_property_management_fee: data.current_annual_property_management_fee != null ? String(data.current_annual_property_management_fee) : (data.extra_info?.current_annual_property_management_fee ? String(data.extra_info.current_annual_property_management_fee) : ""),
                        features: data.extra_info?.features || "",
                        internal_features: data.internal_features || data.extra_info?.internal_features || "",
                        external_features: data.external_features || data.extra_info?.external_features || "",
                        weekly_rent: data.weekly_rent != null ? data.weekly_rent : (data.extra_info?.weekly_rent ?? ""),
                        other_info: data.extra_info?.other_info || "",
                        // Default published_on_website=true when method_of_sale is a buyer-portal value
                        // so property appears in Auction/Private Sale/House & Land tabs
                        published_on_website: (() => {
                            if (data.extra_info?.published_on_website !== undefined) return data.extra_info.published_on_website;
                            const method = (data.extra_info?.method || data.method_of_sale || "").toLowerCase();
                            const buyerPortalMethods = ["auction", "private treaty", "private sale", "house and land"];
                            return buyerPortalMethods.some(m => method.includes(m));
                        })(),
                        published_on_rea: data.extra_info?.published_on_rea || false,
                        published_on_realcommercial: data.published_on_realcommercial || data.extra_info?.published_on_realcommercial || false,
                        published_on_domain: data.extra_info?.published_on_domain || false,
                        // Owner information for AddPropertyForm
                        owner: {
                            name: data.extra_info?.owner_name || "",
                            preferred_name: data.extra_info?.owner_preferred_name || "",
                            contact: data.extra_info?.owner_contact || "",
                            email: data.extra_info?.owner_email || "",
                        },
                        additionalOwners: data.extra_info?.additional_owners || [],
                    };

                    setInitialValues(formValues);
                    // Store REA identifiers and postcode for update - required for third-party sync
                    setExistingReaData({
                        listing_id: data.listing_id,
                        unique_id: data.extra_info?.unique_id || (data.listing_id ? `rea_id ${data.listing_id}` : null),
                        postcode: data.postcode || "",
                    });
                } catch (error) {
                    console.error("Fetch error:", error);
                    toast.error("Failed to load property details");
                } finally {
                    setIsLoading(false);
                }
            };
            fetchProperty();
        }
    }, [id]);

    // Helper function to prepare FormData for property-management
    const preparePropertyFormData = (collingsPropertyData, sanitizedPropertyData) => {
        const formData = new FormData();

        // Map property type
        const mapPropertyTypeToCategory = (propertyType) => {
            const mapping = {
                'Apartment': 'apartment',
                'Unit': 'apartment',
                'House': 'house',
                'Townhouse': 'townhouse',
                'Villa': 'house',
                'House and Land': 'house',
                'Land': 'land',
                'Block of Units': 'apartment'
            };
            return mapping[propertyType] || 'house';
        };

        // Basic Information - prioritize sanitizedPropertyData, fallback to collingsPropertyData if provided
        const title = sanitizedPropertyData?.title || collingsPropertyData?.headline || "Property";
        formData.append("title", title);
        formData.append("address", sanitizedPropertyData?.address || collingsPropertyData?.street || "");
        formData.append("suburb", sanitizedPropertyData?.suburb || sanitizedPropertyData?.city || collingsPropertyData?.suburb || "");
        formData.append("city", sanitizedPropertyData?.suburb || sanitizedPropertyData?.city || collingsPropertyData?.suburb || "");
        formData.append("state", sanitizedPropertyData?.state || collingsPropertyData?.state || "");
        formData.append("postal_code", sanitizedPropertyData?.postal_code || collingsPropertyData?.postcode || "");

        // Property Details
        formData.append("property_type", mapPropertyTypeToCategory(sanitizedPropertyData?.propertyTypes || "House"));
        formData.append("beds", sanitizedPropertyData?.beds || collingsPropertyData?.bedrooms || 0);
        formData.append("baths", sanitizedPropertyData?.baths || collingsPropertyData?.bathrooms || 0);

        // Parking - split the typed parking_list by type (garage/carport/open),
        // same as AddPropertyPage's own save, instead of collapsing everything
        // into one "garages" number.
        const parkingTotals = deriveParkingTotals(sanitizedPropertyData);
        const parkingValue = (parkingTotals.garage + parkingTotals.carport + parkingTotals.open) || deriveParkingTotal(sanitizedPropertyData) || collingsPropertyData?.garages || 0;
        formData.append("parkings", parkingTotals.garage || parkingValue);
        formData.append("car_ports", parkingTotals.carport);
        formData.append("open_spaces", parkingTotals.open);

        formData.append("description", sanitizedPropertyData?.description || collingsPropertyData?.description || "");

        // Price and Status
        const price = sanitizedPropertyData?.price || collingsPropertyData?.price || "0";
        formData.append("price", price.toString().replace(/[$,]/g, ""));
        // Derive status from the listing type selected in the initial modal when not explicitly set
        // This fixes the bug where off-market properties were saved with "On Market Listing" status
        const derivedStatus = sanitizedPropertyData?.status ||
            getInitialStatusForListingType(selectedListingType) ||
            "On Market Listing";
        formData.append("status", derivedStatus);

        // Owner information
        formData.append("owner_name", sanitizedPropertyData?.agent || "");
        formData.append("owner_preferred_name", sanitizedPropertyData?.owner_preferred_name || "");
        formData.append("contact", sanitizedPropertyData?.contact || "");
        formData.append("email", sanitizedPropertyData?.email || "");

        // Method of Sale
        formData.append("method_of_sale", sanitizedPropertyData?.method_of_sale || "Private Sale");

        // Transaction type
        formData.append("transaction_type", sanitizedPropertyData?.transaction_type || "sale");

        // Primary Agent
        if (sanitizedPropertyData?.agentId) {
            formData.append("agent", sanitizedPropertyData.agentId);
        }

        // Secondary Agent (Co-Agent) - backend expects 'co_agent'
        if (sanitizedPropertyData?.secondaryAgent) {
            formData.append("co_agent", sanitizedPropertyData.secondaryAgent);
        }

        // Third Agent (access only, not shown on marketing) - backend 'tertiary_agent'
        if (sanitizedPropertyData?.tertiaryAgent) {
            formData.append("tertiary_agent", sanitizedPropertyData.tertiaryAgent);
        }

        // Images - append newly-uploaded File objects directly, and pass any existing
        // image URLs (already on S3 from the Collings/REA record) as copy_image_urls so
        // the backend fetches + attaches them. Without this, editing/creating the linked
        // buyer-portal Property dropped every existing image (only freshly uploaded files
        // survived), which is why images "disappeared" on save.
        if (sanitizedPropertyData?.images && sanitizedPropertyData.images.length > 0) {
            const existingImageUrls = [];
            sanitizedPropertyData.images.forEach((img) => {
                if (img instanceof File) {
                    formData.append("images", img);
                } else if (img && typeof img === 'object' && img.file instanceof File) {
                    formData.append("images", img.file);
                } else if (typeof img === 'string' && img.startsWith('http')) {
                    existingImageUrls.push(img);
                } else if (img && typeof img === 'object' && typeof img.file === 'string' && img.file.startsWith('http')) {
                    existingImageUrls.push(img.file);
                }
            });

            if (existingImageUrls.length > 0) {
                formData.append("copy_image_urls", JSON.stringify(existingImageUrls));
            }
        }

        // Additional fields
        if (sanitizedPropertyData?.land_area) {
            formData.append("land_size", sanitizedPropertyData.land_area);
        }
        if (sanitizedPropertyData?.floor_area) {
            formData.append("building_size", sanitizedPropertyData.floor_area);
        }
        if (sanitizedPropertyData?.parking_type) {
            formData.append("parking_type", sanitizedPropertyData.parking_type);
        }
        // Send parking_spaces for extra_info (using the consolidated value)
        if (parkingValue) {
            formData.append("parking_spaces", parkingValue);
        }
        if (sanitizedPropertyData?.year_built) {
            formData.append("year_built", sanitizedPropertyData.year_built);
        }
        if (sanitizedPropertyData?.property_class) {
            formData.append("property_class", sanitizedPropertyData.property_class);
        }
        // realcommercial.com.au categories (up to 3) for commercial listings.
        if (sanitizedPropertyData?.commercial_category) {
            formData.append("commercial_category", sanitizedPropertyData.commercial_category);
        }
        if (sanitizedPropertyData?.commercial_category_2) {
            formData.append("commercial_category_2", sanitizedPropertyData.commercial_category_2);
        }
        if (sanitizedPropertyData?.commercial_category_3) {
            formData.append("commercial_category_3", sanitizedPropertyData.commercial_category_3);
        }
        // Only append URL fields if they're not empty strings (to avoid validation errors)
        if (sanitizedPropertyData?.video_url && sanitizedPropertyData.video_url.trim() !== "") {
            formData.append("video_url", sanitizedPropertyData.video_url);
        }
        if (sanitizedPropertyData?.virtual_tour_link && sanitizedPropertyData.virtual_tour_link.trim() !== "") {
            formData.append("virtual_tour_link", sanitizedPropertyData.virtual_tour_link);
        }
        if (sanitizedPropertyData?.auction_date) {
            // Same shared AddPropertyForm as AddPropertyPage - its auction_date
            // is a bare datetime-local "YYYY-MM-DDTHH:MM" (Melbourne wall-clock
            // time, no timezone marker). Django's TIME_ZONE is UTC, so this must
            // be converted before it reaches the property_management REA push,
            // or the auction time comes out 10-11 hours wrong on the live listing.
            const auctionUtc = datetimeLocalToUtcISOString(sanitizedPropertyData.auction_date);
            if (auctionUtc) formData.append("auction_date", auctionUtc);
        }
        if (sanitizedPropertyData?.auction_venue) {
            formData.append("auction_venue", sanitizedPropertyData.auction_venue);
        }
        // Commission fields - REQUIRED
        const commissionType = sanitizedPropertyData?.commission_type || "percentage";
        formData.append("commission_type", commissionType);

        if (commissionType === "fixed") {
            if (!sanitizedPropertyData?.commission_amount) {
                throw new Error("Commission amount is required when commission type is 'fixed'");
            }
            formData.append("commission_amount", sanitizedPropertyData.commission_amount.replace(/[$,]/g, ""));
        } else {
            if (!sanitizedPropertyData?.commission_rate) {
                throw new Error("Commission rate is required when commission type is 'percentage'");
            }
            formData.append("commission_rate", sanitizedPropertyData.commission_rate);
        }

        // Add financial fields
        if (sanitizedPropertyData?.est_gross_yield) {
            formData.append("est_gross_yield", sanitizedPropertyData.est_gross_yield);
        }
        if (sanitizedPropertyData?.est_outgoings) {
            formData.append("est_outgoings", sanitizedPropertyData.est_outgoings);
        }
        if (sanitizedPropertyData?.est_net_yield) {
            formData.append("est_net_yield", sanitizedPropertyData.est_net_yield);
        }

        // Add outgoings breakdown fields - ensure currency formatting is removed
        if (sanitizedPropertyData?.body_corporate) {
            formData.append("body_corporate", String(sanitizedPropertyData.body_corporate).replace(/[$,]/g, ""));
        }
        if (sanitizedPropertyData?.council_rates) {
            formData.append("council_rates", String(sanitizedPropertyData.council_rates).replace(/[$,]/g, ""));
        }
        if (sanitizedPropertyData?.water_rates) {
            formData.append("water_rates", String(sanitizedPropertyData.water_rates).replace(/[$,]/g, ""));
        }
        if (sanitizedPropertyData?.current_annual_property_management_fee) {
            formData.append("current_annual_property_management_fee", String(sanitizedPropertyData.current_annual_property_management_fee).replace(/[$,]/g, ""));
        }

        // Features - required by property-management; use extra_info.features for Collings
        const featuresVal = sanitizedPropertyData?.features ||
            collingsPropertyData?.extra_info?.features ||
            sanitizedPropertyData?.extra_info?.features ||
            "";
        formData.append("features", (featuresVal && String(featuresVal).trim()) ? String(featuresVal).trim() : "N/A");

        // Publishing status - CRITICAL for buyer portal visibility
        // Always send so property appears in correct method-of-sale tab (Auction/Private Sale/House & Land)
        // For Collings properties, default to true if method_of_sale is a buyer portal method
        const method = (sanitizedPropertyData?.method_of_sale || "").toLowerCase();
        const buyerPortalMethods = ["auction", "private treaty", "private sale", "house and land"];
        const isBuyerPortalMethod = buyerPortalMethods.some(m => method.includes(m));

        // Use explicit value if set, otherwise default to true for buyer portal methods
        const shouldPublish = sanitizedPropertyData?.published_on_website !== undefined
            ? sanitizedPropertyData.published_on_website
            : isBuyerPortalMethod;

        // Convert boolean to string for FormData (Django expects string "true"/"false")
        formData.append("published_on_website", shouldPublish ? "true" : "false");
        // Commercial listings carry a Real Commercial visibility flag; send it so
        // ticking realcommercial.com.au in the edit form actually publishes.
        formData.append("published_on_realcommercial", sanitizedPropertyData?.published_on_realcommercial ? "true" : "false");

        // Flag to indicate this property is from Collings (allows creation without images)
        formData.append("from_collings", "true");

        // Link this CRM Property to the RealEstateProperty we just created in the
        // Collings/REA portal. Without this link, CollingsPropertiesPage can't
        // dedupe the two rows and shows the property twice (once per source).
        const reaListingId =
            sanitizedPropertyData?.rea_listing_id ||
            collingsPropertyData?.rea_listing_id ||
            collingsPropertyData?.listing_id ||
            "";
        if (reaListingId) {
            formData.append("rea_listing_id", reaListingId);
        }

        return formData;
    };

    // Helper function to create property-management property from Collings property data
    // collingsPropertyData can be null - in that case, use only sanitizedPropertyData
    const createPropertyManagementProperty = async (collingsPropertyData, sanitizedPropertyData) => {
        try {
            const formData = preparePropertyFormData(collingsPropertyData, sanitizedPropertyData);
            const response = await registerProperty(formData);

            // Extract property ID using same logic as AddPropertyPage
            const extractPropertyId = (res) => {
                if (!res) return null;
                const d = res.data;
                const h = res.headers;
                const candidates = [
                    d?.id,
                    d?.property?.id,
                    d?.created_id,
                    d?.property_id,
                    d?.data?.id,
                    d?.result?.id,
                ].filter((v) => v !== undefined && v !== null);
                if (candidates.length > 0) {
                    return candidates[0];
                }
                const location = h?.location || h?.Location;
                if (typeof location === "string") {
                    const match = location.match(/\/(\d+)(?:\/?$|\b)/);
                    if (match && match[1]) {
                        return Number(match[1]);
                    }
                }
                return null;
            };

            const propertyManagementId = extractPropertyId(response);

            if (!propertyManagementId) {
                console.error("Failed to get property-management property ID from response:", response.data);
                throw new Error("Could not determine created property ID");
            }

            return propertyManagementId;
        } catch (error) {
            console.error("Error creating property-management property:", error);
            throw error;
        }
    };

    // AddPropertyForm's own submit calls onSubmit(submissionData, formData) - the
    // Floorplan checkbox's state (floorplan_flags, parallel to newly-uploaded
    // files only) lives on `formData`, not `submissionData`, so a handler that
    // only takes one argument silently drops it. That's exactly what happened
    // here: this page's handleSubmit only ever took `propertyData`, so ticking
    // "Floorplan" on a new upload from the Collings Properties add/edit form
    // never reached the backend at all (confirmed live, 2026-08-14: 8/118
    // Miller Street). This extracts the newly-uploaded files flagged as a
    // floorplan out of that dropped `formData`, for the caller to push via the
    // dedicated floorplan endpoint once the property id is known.
    const extractNewFloorplanFiles = (formData) => {
        if (!formData || typeof formData.getAll !== "function") return [];
        try {
            const flagsRaw = formData.get("floorplan_flags");
            const flags = flagsRaw ? JSON.parse(flagsRaw) : [];
            const entries = formData.getAll("images");
            const newFiles = [];
            let flagIdx = 0;
            entries.forEach((entry) => {
                if (entry instanceof File) {
                    if (flags[flagIdx]) newFiles.push(entry);
                    flagIdx += 1;
                }
            });
            return newFiles;
        } catch (e) {
            return [];
        }
    };

    const handleSubmit = async (propertyData, formData) => {
        try {
            setIsUpdating(true);

            // Validate property data before submission
            const validation = validateCollingsProperty(propertyData);
            if (!validation.isValid) {
                // Show validation errors
                const errorMessage = validation.errors.length === 1
                    ? validation.errors[0]
                    : validation.errors.map((err, idx) => `${idx + 1}. ${err}`).join('\n');

                toast.error(errorMessage, {
                    id: "validation-toast",
                    duration: 8000
                });
                setIsUpdating(false);
                return;
            }

            // Sanitize property data to prevent encoding issues
            const sanitizedPropertyData = sanitizePropertyData(propertyData);

            // Map frontend property types to Collings broad categories
            const mapPropertyTypeToCategory = (propertyType) => {
                const mapping = {
                    'Apartment': 'residential',
                    'Unit': 'residential',
                    'House': 'residential',
                    'Townhouse': 'residential',
                    'Villa': 'residential',
                    'House and Land': 'residential',
                    'Land': 'land',
                    'Block of Units': 'residential',
                    'Off the Plan Apartments': 'residential',
                    // Commercial must map to 'commercial' so the REAXML feed wraps it in
                    // <commercial> — otherwise REA rejects it as an unrecognised category.
                    'Commercial': 'commercial'
                };
                return mapping[propertyType] || 'residential';
            };

            // Map method of sale to standard values
            const methodOfSale = sanitizedPropertyData?.method_of_sale || "Private Sale";
            const standardMethod = methodOfSale; // Use as-is: Auction, Private Sale, House and Land

            // Prepare extra_info with all additional fields (using sanitized data)
            const extraInfo = {
                // Market status (on_market/off_market)
                status: (sanitizedPropertyData?.status || "").toLowerCase().includes("off market") ? "offmarket" :
                    (sanitizedPropertyData?.status || "").toLowerCase().includes("prospect") ? "prospect" :
                        (sanitizedPropertyData?.status || "").toLowerCase().includes("appraisal") ? "appraisal" : "current",

                // Owner information
                owner_name: sanitizedPropertyData?.agent || "",
                owner_preferred_name: sanitizedPropertyData?.owner_preferred_name || "",
                owner_contact: sanitizedPropertyData?.contact || "",
                owner_email: sanitizedPropertyData?.email || "",

                // Parking details — use the typed parking_list total so the car count
                // round-trips (the form no longer populates the legacy `parking` field).
                parking_type: sanitizedPropertyData?.parking_type || "",
                parking_spaces: deriveParkingTotal(sanitizedPropertyData) || "",

                // Property details
                year_built: sanitizedPropertyData?.year_built || "",
                property_class: sanitizedPropertyData?.property_class || "",
                floor_area: sanitizedPropertyData?.floor_area || "",
                land_area: sanitizedPropertyData?.land_area || "",

                // Media
                video_url: sanitizedPropertyData?.video_url || "",
                virtual_tour_link: sanitizedPropertyData?.virtual_tour_link || "",

                // Transaction details
                transaction_type: sanitizedPropertyData?.transaction_type || "sale",
                price_display: sanitizedPropertyData?.price_display || "exact",

                // Auction details
                auction_date: sanitizedPropertyData?.auction_date || "",
                auction_venue: sanitizedPropertyData?.auction_venue || "",

                // Financial details — commission: percentage or fixed amount (agents pick one)
                commission_type: sanitizedPropertyData?.commission_type || "percentage",
                commission_rate: sanitizedPropertyData?.commission_type === "fixed" ? "" : (sanitizedPropertyData?.commission_rate || ""),
                commission_amount: sanitizedPropertyData?.commission_type === "fixed" && sanitizedPropertyData?.commission_amount
                    ? String(sanitizedPropertyData.commission_amount).replace(/[$,]/g, "") : "",
                est_gross_yield: sanitizedPropertyData?.est_gross_yield || "",
                est_outgoings: sanitizedPropertyData?.est_outgoings || "",
                est_net_yield: sanitizedPropertyData?.est_net_yield || "",

                // Outgoings breakdown
                body_corporate: sanitizedPropertyData?.body_corporate || "",
                council_rates: sanitizedPropertyData?.council_rates || "",
                water_rates: sanitizedPropertyData?.water_rates || "",
                current_annual_property_management_fee: sanitizedPropertyData?.current_annual_property_management_fee || "",

                // Additional info
                features: sanitizedPropertyData?.features || "",
                other_info: sanitizedPropertyData?.other_info || "",
                additional_owners: sanitizedPropertyData?.additionalOwners || [],
                // Agent IDs for Collings properties (for backward compatibility)
                agent_id: sanitizedPropertyData?.agentId || "",
                co_agent_id: sanitizedPropertyData?.secondaryAgent || "",
                tertiary_agent_id: sanitizedPropertyData?.tertiaryAgent || "",
                // Store full agent objects in extra_info (like public properties)
                // These will be populated by backend when saving

                // Images: preserve existing URLs (for REA update) + new Files sent separately
                images: (sanitizedPropertyData?.images || [])
                    .map(img => typeof img === 'string' ? img : (img?.file && typeof img.file === 'string' ? img.file : img?.file))
                    .filter(Boolean) || [],

                // Publishing status
                published_on_website: sanitizedPropertyData?.published_on_website || false,
                published_on_rea: sanitizedPropertyData?.published_on_rea || false,
                published_on_domain: sanitizedPropertyData?.published_on_domain || false,

                // Method of sale (REA expects method_of_sale in extra_info)
                method: standardMethod,
                method_of_sale: standardMethod,
            };

            // For UPDATE: preserve unique_id (and listing_id) so REA third-party syncs UPDATE not duplicate
            if (id && existingReaData) {
                if (existingReaData.unique_id) extraInfo.unique_id = existingReaData.unique_id;
                if (existingReaData.listing_id) extraInfo.listing_id = existingReaData.listing_id;
            }

            // Map generic property data to Collings structure (using sanitized data)
            const collingsData = {
                headline: sanitizedPropertyData?.title || "",
                description: sanitizedPropertyData?.description || "",
                price: (sanitizedPropertyData?.price || "0").toString().replace(/[$,]/g, ""),
                bedrooms: sanitizedPropertyData?.beds || 0,
                bathrooms: sanitizedPropertyData?.baths || 0,
                // Split by type (garage/carport/open) instead of one combined
                // number - a listing entered as "1 garage + 1 carport" (2
                // total) previously only ever sent garages=1 to REA, which
                // showed as "1 car spot" (CG report, 2026-08-06).
                garages: deriveParkingTotals(sanitizedPropertyData).garage || deriveParkingTotal(sanitizedPropertyData),
                carports: deriveParkingTotals(sanitizedPropertyData).carport,
                open_spaces: deriveParkingTotals(sanitizedPropertyData).open,
                // Map specific property type to broad Collings category
                property_type: mapPropertyTypeToCategory(sanitizedPropertyData?.propertyTypes || "House"),
                // Keep the specific property type in category field
                category: sanitizedPropertyData?.propertyTypes || "House",
                street_number: splitStreetLine(sanitizedPropertyData?.address).streetNumber,
                street: (sanitizedPropertyData?.street || "").trim() || splitStreetLine(sanitizedPropertyData?.address).street,
                suburb: sanitizedPropertyData?.suburb || sanitizedPropertyData?.city || "",
                state: sanitizedPropertyData?.state || "",
                // REA requires 4-digit postcode - use existing if form has invalid
                postcode: (() => {
                    const pc = (sanitizedPropertyData?.postal_code || "").toString().trim();
                    if (pc.length === 4 && /^\d{4}$/.test(pc)) return pc;
                    if (id && existingReaData?.postcode) return existingReaData.postcode;
                    return pc || "";
                })(),
                status: sanitizedPropertyData?.status || "On Market Listing",
                agent_id: sanitizedPropertyData?.agentId || "",
                co_agent_id: sanitizedPropertyData?.secondaryAgent || "",
                tertiary_agent_id: sanitizedPropertyData?.tertiaryAgent || "",
                // Images: URL strings (existing) + File objects (new) - updateREAProperty needs both
                images: (sanitizedPropertyData?.images || []).map(img =>
                    typeof img === 'string' ? img : (img instanceof File ? img : (img?.file && typeof img.file === 'string' ? img.file : img?.file))
                ).filter(Boolean),
                // Add missing fields for Collings properties
                method_of_sale: standardMethod,
                parking_type: sanitizedPropertyData?.parking_type || "",
                parking_spaces: deriveParkingTotal(sanitizedPropertyData) || "",
                floor_area: sanitizedPropertyData?.floor_area || "",
                land_area: sanitizedPropertyData?.land_area || "",
                property_class: sanitizedPropertyData?.property_class || "",
                // Add extra_info with all additional fields
                extra_info: extraInfo,
            };

            // Show realestate.com.au confirmation modal only when REA is actually selected
            if (!reaConfirmedRef.current && sanitizedPropertyData?.published_on_rea) {
                setPendingCollingsData(propertyData);
                setShowReaConfirm(true);
                setIsUpdating(false);
                return;
            }
            reaConfirmedRef.current = false;

            if (id) {
                // Update existing property - Use PUT endpoint (not POST) to UPDATE not create duplicate
                setIsUploading(true);
                toast.loading("Updating property in Collings third-party platform...", { id: "upload-toast" });

                // Use updateCollingsProperty (PUT) which updates existing property and returns upload_id for polling
                const uploadResponse = await updateCollingsProperty(id, collingsData);
                const uploadId = uploadResponse.data?.upload_id;

                // Push any newly-uploaded photo flagged as a floorplan (see
                // extractNewFloorplanFiles above for why this can't just ride
                // along inside collingsData).
                const newFloorplanFiles = extractNewFloorplanFiles(formData);
                if (newFloorplanFiles.length > 0) {
                    try {
                        await uploadCollingsPropertyFloorplan(id, newFloorplanFiles);
                    } catch (fpError) {
                        console.error("Failed to tag floorplan:", fpError);
                        toast.error(
                            fpError?.response?.data?.detail || "Photo saved, but marking it as a floorplan failed."
                        );
                    }
                }
                // Carry the REA listing_id forward so any fallback CRM Property
                // creation below gets linked via rea_listing_id (avoids duplicates).
                const existingReaListingId =
                    existingReaData?.listing_id ||
                    existingReaData?.unique_id ||
                    uploadResponse.data?.listing_id ||
                    "";
                if (existingReaListingId) {
                    sanitizedPropertyData.rea_listing_id = existingReaListingId;
                }

                if (!uploadId) {
                    // No upload_id means either error or immediate success (no third-party sync needed).
                    // Reaching here at all means a 2xx response (axios throws on 4xx → caught below),
                    // so a missing upload_id is a successful local save that just skipped REA polling.
                    const updateDetail = uploadResponse.data?.detail || "";

                    // The BE returns several "success" detail strings depending on the REA branch:
                    //   • "Local update successful, REA sync skipped"
                    //   • "Property updated locally — realestate.com.au is not selected ... REA sync was skipped"
                    //   • "Property updated locally and synced to REA platform"
                    // The old check only matched the first, so the REA-not-selected case fell through to
                    // the error branch — which then ran the WHOLE serialized property object through the
                    // error formatter, whose postcode regex grabbed a stray number and produced a bogus
                    // "Invalid Postcode" message even though the save (and postcode) were fine.
                    const localUpdateOk = /updated locally|saved locally|sync was skipped|sync skipped|Local update successful|successfully/i.test(updateDetail);

                    // Check if it's a sync detail (local update successful, REA sync skipped/backgrounded)
                    if (localUpdateOk) {
                        toast.success("Property uploaded successfully.", {
                            id: "upload-toast",
                            duration: 5000
                        });
                        setIsUploading(false);

                        // Still try to sync to local Property for buyer portal
                        try {
                            const { getProperties } = require("../../../api/agent");
                            const searchRes = await getProperties({ search: sanitizedPropertyData.title || sanitizedPropertyData.address });
                            const localProp = searchRes.data?.results?.find(p =>
                                p.title === (sanitizedPropertyData.title || "") &&
                                p.address === (sanitizedPropertyData.address || "")
                            );
                            if (localProp) {
                                const { updateProperty } = require("../../../api/agent");
                                const formData = preparePropertyFormData(null, sanitizedPropertyData);
                                await updateProperty(localProp.id, formData);
                            }
                        } catch (err) {
                            console.error("Error syncing local property:", err);
                        }

                        navigate("/collings-properties");
                        return;
                    }

                    // Genuine error path (2xx but a real "REA sync failed" style detail).
                    // Only format the detail/errors here — never the full serialized property
                    // object, whose numeric field values trip the postcode regex in the formatter.
                    const errorPayload = uploadResponse.data?.errors ||
                        uploadResponse.data?.rea_errors ||
                        updateDetail ||
                        uploadResponse.data;
                    const errorMessage = formatErrorForToast(errorPayload, 1000);
                    toast.error(errorMessage, {
                        id: "upload-toast",
                        duration: 10000
                    });
                    setIsUploading(false);
                    return;
                }

                toast.loading("Processing update with Collings...", { id: "upload-toast" });

                // Poll for upload status - same as ADD flow
                let completed = false;
                let attempts = 0;
                let consecutiveErrors = 0;
                const maxAttempts = 40;
                const maxConsecutiveErrors = 2;

                while (!completed && attempts < maxAttempts) {
                    await new Promise(resolve => setTimeout(resolve, 3000));

                    try {
                        const statusResponse = await checkUploadStatus(uploadId);
                        completed = statusResponse.data?.completed || false;

                        console.log(`Upload status check (attempt ${attempts + 1}/${maxAttempts}):`, {
                            completed,
                            detail: statusResponse.data?.detail,
                            hasErrors: !!(statusResponse.data?.errors || statusResponse.data?.collings_errors || statusResponse.data?.rea_errors)
                        });

                        const hasErrors = (statusResponse.data?.errors && statusResponse.data.errors.length > 0) ||
                            (statusResponse.data?.collings_errors && statusResponse.data.collings_errors.length > 0) ||
                            (statusResponse.data?.rea_errors && statusResponse.data.rea_errors.length > 0);

                        const detail = statusResponse.data?.detail || "";
                        const isSuccess = detail.includes("successfully") || detail.toLowerCase().includes("uploaded successfully");
                        const isProcessing = detail.includes("processing") || detail.includes("still processing");

                        const isErrorResponse = statusResponse.status >= 400 ||
                            (completed && !isSuccess && (hasErrors || (!isProcessing && detail))) ||
                            (hasErrors && !isSuccess);

                        if (completed) {
                            if (isSuccess) {
                                setIsUploading(false);
                                toast.dismiss("upload-toast");

                                // Update local Property record (for Buyer Portal)
                                try {
                                    toast.loading("Syncing to Collings Portal...", { id: "property-sync-toast" });

                                    const { getProperties } = require("../../../api/agent");
                                    const searchRes = await getProperties({ search: sanitizedPropertyData.title || sanitizedPropertyData.address });

                                    const localProp = searchRes.data?.results?.find(p =>
                                        p.title === (sanitizedPropertyData.title || "") &&
                                        p.address === (sanitizedPropertyData.address || "")
                                    );

                                    if (localProp) {
                                        const { updateProperty } = require("../../../api/agent");
                                        const formData = preparePropertyFormData(null, sanitizedPropertyData);
                                        await updateProperty(localProp.id, formData);
                                        console.log("Synced update to local property:", localProp.id);
                                    } else {
                                        console.log("Local property not found, creating new...");
                                        await createPropertyManagementProperty(null, sanitizedPropertyData);
                                    }

                                    toast.dismiss("property-sync-toast");
                                    toast.success("Property updated successfully in third-party and Collings Portal!", {
                                        duration: 3000
                                    });
                                    navigate("/collings-properties");
                                } catch (propertyError) {
                                    console.error("Error syncing local property:", propertyError);
                                    toast.dismiss("property-sync-toast");
                                    toast.success("Property updated in third-party. Collings Portal sync may be delayed.", {
                                        duration: 5000
                                    });
                                    navigate("/collings-properties");
                                }
                                return;
                            } else {
                                setIsUploading(false);
                                const errorMessage = formatErrorForToast(statusResponse.data, 1000);
                                toast.error(errorMessage, {
                                    id: "upload-toast",
                                    duration: 12000
                                });
                                return;
                            }
                        } else if (isErrorResponse) {
                            consecutiveErrors++;
                            if (consecutiveErrors >= maxConsecutiveErrors) {
                                setIsUploading(false);
                                const errorMessage = formatErrorForToast(statusResponse.data, 1000);
                                toast.error(errorMessage, {
                                    id: "upload-toast",
                                    duration: 12000
                                });
                                return;
                            }
                        } else {
                            consecutiveErrors = 0;
                        }
                    } catch (statusError) {
                        consecutiveErrors++;
                        if (consecutiveErrors >= maxConsecutiveErrors) {
                            setIsUploading(false);
                            const errorMsg = formatErrorForToast(
                                statusError?.response?.data || statusError,
                                1000
                            );
                            toast.error(errorMsg, {
                                id: "upload-toast",
                                duration: 10000
                            });
                            return;
                        }

                        if (attempts === maxAttempts - 1) {
                            setIsUploading(false);
                            const errorMsg = formatErrorForToast(
                                statusError?.response?.data || statusError,
                                1000
                            );
                            toast.error(errorMsg, {
                                id: "upload-toast",
                                duration: 10000
                            });
                            return;
                        }
                    }

                    attempts++;
                }

                if (!completed) {
                    setIsUploading(false);
                    toast.error("Property update timed out. Please check the properties list later or try resubmitting.", {
                        id: "upload-toast",
                        duration: 8000
                    });
                }
            } else {
                // Create new property with status polling
                setIsUploading(true);
                toast.loading("Uploading property to Collings...", { id: "upload-toast" });

                const uploadResponse = await importCollingsProperty(collingsData);
                const uploadId = uploadResponse.data?.upload_id;
                // Capture the local REA listing_id so the CRM Property we create
                // after polling can be linked back via rea_listing_id (prevents the
                // Collings Properties page from showing the property twice).
                const reaListingIdForLink = uploadResponse.data?.listing_id || "";
                if (reaListingIdForLink) {
                    sanitizedPropertyData.rea_listing_id = reaListingIdForLink;
                }

                // BE returns 201 with listing_id but no upload_id when the user
                // didn't tick realestate.com.au in Visibility — local save succeeded,
                // REA push was intentionally skipped. Treat as success, not error.
                const uploadDetail = uploadResponse.data?.detail || "";
                const reaSyncSkipped = !uploadId && reaListingIdForLink &&
                    /saved locally|sync was skipped/i.test(uploadDetail);

                if (reaSyncSkipped) {
                    toast.dismiss("upload-toast");
                    toast.success("Property saved — REA sync skipped (realestate.com.au not selected in Visibility).", {
                        duration: 4000
                    });

                    try {
                        toast.loading("Creating property record...", { id: "property-create-toast" });
                        const propertyManagementId = await createPropertyManagementProperty(null, sanitizedPropertyData);
                        toast.dismiss("property-create-toast");

                        if (propertyManagementId) {
                            setCreatedPropertyId(propertyManagementId);
                        } else {
                            navigate("/collings-properties");
                        }
                    } catch (propertyError) {
                        console.error("Error creating property-management record:", propertyError);
                        toast.dismiss("property-create-toast");
                        // Say WHY: a 409 from the register endpoint names the
                        // address it clashed with; a bare "failed" sent agents
                        // re-keying the whole form on the edit page.
                        toast.error(localRecordFailureMessage("Property saved", propertyError), {
                            duration: 10000
                        });
                        navigate("/collings-properties");
                    }
                    setIsUploading(false);
                    return;
                }

                if (!uploadId) {
                    // Extract and format errors from upload response
                    const errorMessage = formatErrorForToast(uploadResponse.data || uploadResponse, 1000);

                    toast.error(errorMessage, {
                        id: "upload-toast",
                        duration: 10000
                    });
                    setIsUploading(false);
                    // Don't navigate away - let user fix errors and resubmit
                    return;
                }

                toast.loading("Processing with Collings...", { id: "upload-toast" });

                // Poll for upload status - Collings takes longer, so use longer polling
                let completed = false;
                let attempts = 0;
                let consecutiveErrors = 0;
                const maxAttempts = 40; // 40 attempts * 3 seconds = 120 seconds max (2 minutes for Collings)
                const maxConsecutiveErrors = 2; // Stop after 2 consecutive errors

                while (!completed && attempts < maxAttempts) {
                    await new Promise(resolve => setTimeout(resolve, 3000)); // Wait 3 seconds (longer for Collings)

                    try {
                        const statusResponse = await checkUploadStatus(uploadId);
                        completed = statusResponse.data?.completed || false;

                        console.log(`Upload status check (attempt ${attempts + 1}/${maxAttempts}):`, {
                            completed,
                            detail: statusResponse.data?.detail,
                            hasErrors: !!(statusResponse.data?.errors || statusResponse.data?.collings_errors || statusResponse.data?.rea_errors)
                        });

                        // Check if response indicates an error
                        const hasErrors = (statusResponse.data?.errors && statusResponse.data.errors.length > 0) ||
                            (statusResponse.data?.collings_errors && statusResponse.data.collings_errors.length > 0) ||
                            (statusResponse.data?.rea_errors && statusResponse.data.rea_errors.length > 0);

                        const detail = statusResponse.data?.detail || "";
                        const isSuccess = detail.includes("successfully") || detail.toLowerCase().includes("uploaded successfully");
                        const isProcessing = detail.includes("processing") || detail.includes("still processing");

                        // If completed with errors or error detail, treat as error
                        const isErrorResponse = statusResponse.status >= 400 ||
                            (completed && !isSuccess && (hasErrors || (!isProcessing && detail))) ||
                            (hasErrors && !isSuccess);

                        if (completed) {
                            if (isSuccess) {
                                // Collings upload successful
                                setIsUploading(false);
                                toast.dismiss("upload-toast");
                                toast.success("Property added successfully to Collings!", {
                                    duration: 3000
                                });

                                try {
                                    // Create property-management property for document upload and buyer portal
                                    toast.loading("Creating property record...", { id: "property-create-toast" });

                                    const propertyManagementId = await createPropertyManagementProperty(null, sanitizedPropertyData);

                                    toast.dismiss("property-create-toast");

                                    if (propertyManagementId) {
                                        // Set property ID to enable document upload step
                                        setCreatedPropertyId(propertyManagementId);
                                        console.log("Property-management record created with ID:", propertyManagementId);
                                        // Don't navigate - let AddPropertyForm advance to step 2
                                    } else {
                                        // If no ID, navigate to list as fallback
                                        console.warn("No property ID returned, navigating to list");
                                        navigate("/collings-properties");
                                    }
                                } catch (propertyError) {
                                    console.error("Error creating property-management record:", propertyError);
                                    toast.dismiss("property-create-toast");
                                    toast.error(localRecordFailureMessage("Property added to Collings", propertyError), {
                                        duration: 10000
                                    });
                                    // Navigate to list since we can't proceed to documents
                                    navigate("/collings-properties");
                                }
                                return;
                            } else {
                                // Completed but failed - show error immediately
                                setIsUploading(false);
                                const errorMessage = formatErrorForToast(statusResponse.data, 1000);

                                toast.error(errorMessage, {
                                    id: "upload-toast",
                                    duration: 12000  // Show longer for detailed errors
                                });

                                // Don't navigate away - let user see errors and resubmit
                                return;
                            }
                        } else if (isErrorResponse) {
                            // Error but not completed yet - count consecutive errors
                            consecutiveErrors++;

                            // If we have 2 consecutive errors, stop and show the error
                            if (consecutiveErrors >= maxConsecutiveErrors) {
                                setIsUploading(false);
                                const errorMessage = formatErrorForToast(statusResponse.data, 1000);

                                toast.error(errorMessage, {
                                    id: "upload-toast",
                                    duration: 12000  // Show longer for detailed errors
                                });

                                // Don't navigate away - let user see errors and resubmit
                                return;
                            }
                            // Otherwise, continue polling (might be transient error)
                        } else {
                            // Reset error counter on successful/processing response
                            consecutiveErrors = 0;
                        }
                    } catch (statusError) {
                        consecutiveErrors++;

                        // If we have 2 consecutive errors, stop and show the error
                        if (consecutiveErrors >= maxConsecutiveErrors) {
                            setIsUploading(false);
                            const errorMsg = formatErrorForToast(
                                statusError?.response?.data || statusError,
                                1000
                            );
                            toast.error(errorMsg, {
                                id: "upload-toast",
                                duration: 10000
                            });
                            return;
                        }

                        // If status check fails, continue polling unless it's the last attempt
                        if (attempts === maxAttempts - 1) {
                            // Last attempt failed - show error but don't navigate
                            setIsUploading(false);
                            const errorMsg = formatErrorForToast(
                                statusError?.response?.data || statusError,
                                1000
                            );
                            toast.error(errorMsg, {
                                id: "upload-toast",
                                duration: 10000
                            });
                            return;
                        }
                    }

                    attempts++;
                }

                if (!completed) {
                    setIsUploading(false);
                    toast.error("Property upload timed out. Please check the properties list later or try resubmitting.", {
                        id: "upload-toast",
                        duration: 8000
                    });
                    // Don't navigate away - let user try again
                }
            }
        } catch (error) {
            console.error("Save error:", error);
            setIsUploading(false);

            // Extract and format detailed error information using utility
            const errorMessage = formatErrorForToast(
                error?.response?.data || error,
                1000
            );

            toast.error(errorMessage, {
                id: "upload-toast",
                duration: 12000  // Show for 12 seconds so user can read detailed errors
            });

            // Don't navigate away on error - let user fix and resubmit
        } finally {
            setIsUpdating(false);
            setIsUploading(false);
        }
    };

    if (isLoading) {
        return <div className="flex justify-center items-center h-screen">Loading...</div>;
    }

    return (
        <>
            {!id && (
                <PropertyTypeModal
                    isOpen={showTypeModal}
                    onClose={() => navigate("/collings-properties")}
                    onSelectType={(type) => {
                        setSelectedListingType(type);
                        setShowTypeModal(false);
                    }}
                />
            )}
            {(id || selectedListingType != null) && (
                <AddPropertyForm
                    onSubmit={handleSubmit}
                    isUpdating={isUpdating}
                    isUploading={isUploading}
                    onClose={() => navigate("/collings-properties")}
                    isCrossIconPress={() => navigate("/collings-properties")}
                    listingType={id ? undefined : selectedListingType}
                    hideFurnished
                    initialValues={
                        id
                            ? initialValues
                            : selectedListingType != null
                                ? { status: getInitialStatusForListingType(selectedListingType) }
                                : null
                    }
                    isEdit={!!id}
                    externalPropertyId={createdPropertyId || id}
                />
            )}
            {/* realestate.com.au confirmation modal for Collings properties */}
            <ConfirmModal
                isOpen={showReaConfirm}
                onClose={() => {
                    setShowReaConfirm(false);
                    setPendingCollingsData(null);
                }}
                onConfirm={() => {
                    reaConfirmedRef.current = true;
                    setShowReaConfirm(false);
                    handleSubmit(pendingCollingsData);
                }}
                title={id ? "Confirm realestate.com.au Update" : "Add to realestate.com.au?"}
                description={
                    id
                        ? "Are you sure you want to update this property on realestate.com.au? The changes will be synced to the Collings platform and published on realestate.com.au."
                        : "Are you sure you want to add this property to realestate.com.au? It will be uploaded to the Collings platform and published on realestate.com.au."
                }
                confirmText={id ? "Yes, Update" : "Yes, Add to REA"}
                cancelText="Cancel"
                variant="primary"
            />
        </>
    );
}
